/**
 * TapeGeeks WebMCP tools — domain-knowledge tools an in-browser AI agent can call.
 *
 * - Registers against document.modelContext (current WebMCP draft; falls back to
 *   navigator.modelContext for older Chrome builds). No-ops silently in browsers
 *   without the API, so the storefront is unaffected.
 * - Read-only only. Cart / checkout / order tools are registered by Shopify's own
 *   adapter (window.Shopify.MCP) — never duplicate those here.
 * - Data lives in webmcp-data.json (same extension), fetched lazily on first call.
 * - Every call fires a GA4 event `webmcp_tool_call` (Shopify analytics cannot see
 *   WebMCP calls) and shows the result on-page so the human sees what the agent did.
 *
 * Deploy: `shopify app deploy` from the project root (extension changes do NOT
 * deploy via git push).
 */
(function () {
  'use strict';

  var GUARD = '__tgWebMCPRegistered';
  if (window[GUARD]) return;

  var mc = (document.modelContext && typeof document.modelContext.registerTool === 'function')
    ? document.modelContext
    : (navigator.modelContext && typeof navigator.modelContext.registerTool === 'function')
      ? navigator.modelContext
      : null;
  if (!mc) return; // WebMCP not available in this browser — do nothing.

  window[GUARD] = true;

  var cfg = window.tgWebMCPConfig || {};
  var DATA_URL = cfg.dataUrl || '/webmcp-data.json';
  var controller = new AbortController();
  var dataPromise = null;

  function loadData() {
    if (!dataPromise && cfg.data && typeof cfg.data === 'object') dataPromise = Promise.resolve(cfg.data); // inline data (tests / evals)
    if (!dataPromise) {
      dataPromise = fetch(DATA_URL, { credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error('webmcp-data ' + r.status); return r.json(); })
        .catch(function (e) { dataPromise = null; throw e; });
    }
    return dataPromise;
  }

  function abs(base, path) {
    if (!path) return null;
    return /^https?:/.test(path) ? path : base + path;
  }

  function product(data, key) {
    var p = data.products[key];
    if (!p) return null;
    return { name: p.title, url: abs(data.base, p.url), price_cad: p.price_cad, form: p.form, notes: p.notes };
  }

  function guidesFor(data, area, condition, limit) {
    var list = (data.guides[area] || []).slice();
    if (condition) {
      var c = String(condition).toLowerCase();
      list.sort(function (a, b) {
        var sa = a.keywords.some(function (k) { return c.indexOf(k) > -1; }) ? 1 : 0;
        var sb = b.keywords.some(function (k) { return c.indexOf(k) > -1; }) ? 1 : 0;
        return sb - sa;
      });
    }
    return list.slice(0, limit || 3).map(function (g) { return { title: g.title, url: abs(data.base, g.url) }; });
  }

  // ---------- on-page confirmation panel ----------
  function showPanel(title, lines, link) {
    try {
      var id = 'tg-webmcp-panel';
      var el = document.getElementById(id);
      if (!el) {
        el = document.createElement('div');
        el.id = id;
        el.setAttribute('role', 'status');
        el.style.cssText = 'position:fixed;left:16px;bottom:16px;z-index:2147483000;max-width:360px;background:#111;color:#fff;font:14px/1.4 -apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;border-left:4px solid #C5D52E;border-radius:8px;padding:12px 36px 12px 14px;box-shadow:0 8px 24px rgba(0,0,0,.35)';
        var x = document.createElement('button');
        x.textContent = '×';
        x.setAttribute('aria-label', 'Close');
        x.style.cssText = 'position:absolute;top:6px;right:8px;background:none;border:0;color:#fff;font-size:20px;cursor:pointer';
        x.onclick = function () { el.remove(); };
        el.appendChild(x);
        var body = document.createElement('div');
        body.className = 'tg-webmcp-body';
        el.appendChild(body);
        document.body.appendChild(el);
      }
      var b = el.querySelector('.tg-webmcp-body');
      b.innerHTML = '';
      var h = document.createElement('div');
      h.style.cssText = 'font-weight:700;margin-bottom:4px';
      h.textContent = 'AI assistant · ' + title;
      b.appendChild(h);
      (lines || []).forEach(function (t) {
        var p = document.createElement('div'); p.textContent = t; b.appendChild(p);
      });
      if (link) {
        var a = document.createElement('a');
        a.href = link.url; a.textContent = link.label || link.url;
        a.style.cssText = 'display:inline-block;margin-top:6px;color:#C5D52E;font-weight:600';
        b.appendChild(a);
      }
    } catch (e) { /* never break the page */ }
  }

  function track(tool, args, ok) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'webmcp_tool_call', {
          tool_name: tool,
          tool_ok: ok ? 1 : 0,
          tool_args: JSON.stringify(args || {}).slice(0, 100),
          page_path: location.pathname
        });
      }
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'webmcp_tool', tool);
        window.clarity('event', 'webmcp_tool_call');
      }
    } catch (e) { /* ignore */ }
  }

  function wrap(name, fn) {
    return function (args, ctx) {
      var a = args || {};
      return loadData().then(function (data) {
        var res = fn(a, data);
        track(name, a, true);
        return res;
      }).catch(function (e) {
        track(name, a, false);
        return { error: 'TapeGeeks tool ' + name + ' failed: ' + (e && e.message ? e.message : String(e)), fallback: 'Ask the Tappy chat bubble on this page, or email admin@tapegeeks.com.' };
      });
    };
  }

  var BODY_AREAS = ['foot_heel', 'ankle', 'shin_calf', 'knee', 'hip_it_band', 'hamstring_quad', 'groin', 'lower_back', 'upper_back_neck', 'shoulder', 'elbow', 'wrist_hand', 'fingers_thumb', 'abdomen', 'general'];

  // ---------- Tool 1: recommend_tape_for_use_case ----------
  var recommendTape = {
    name: 'recommend_tape_for_use_case',
    title: 'Recommend the right TapeGeeks tape',
    description:
      'Recommend which TapeGeeks tape product fits a person\'s body area, goal and situation, and return the matching taping guide. ' +
      'Use this when someone describes a pain, injury, sport or taping need (e.g. "plantar fasciitis and I sweat a lot", "firm ankle support for a game", "grip tape for CrossFit"). ' +
      'Returns product name, URL, price in CAD, why it fits, precut vs roll advice, skin/sweat tips, and guide links. ' +
      'Do NOT use this for adding to cart or checkout (use Shopify\'s update_cart / proceed_to_checkout), for nasal strips or mouth tape (use size_breathe_plus), or for generic catalogue search (use search_catalog).',
    inputSchema: {
      type: 'object',
      properties: {
        body_area: { type: 'string', enum: BODY_AREAS, description: 'Where the person needs support. Use "general" if unknown.' },
        goal: { type: 'string', enum: ['support_and_pain_relief', 'firm_immobilising_support', 'grip_protection', 'compression', 'recovery'], description: 'support_and_pain_relief = flexible support while staying active (most injuries). firm_immobilising_support = lock a joint for a game (ankle/wrist/finger sprains). grip_protection = fingers/palms for lifting or climbing. compression = wrap a swollen area or hold ice. recovery = post-workout soreness.' },
        condition: { type: 'string', description: 'Free-text condition or symptom, e.g. "plantar fasciitis", "runner\'s knee", "tennis elbow". Optional.' },
        sweat_or_water: { type: 'boolean', description: 'True if they sweat heavily, swim, or need the tape to survive showers.' },
        skin_sensitive: { type: 'boolean', description: 'True if they have sensitive skin or reacted to another tape brand.' },
        experience: { type: 'string', enum: ['first_time', 'experienced'], description: 'first_time = never taped before (prefer precut strips).' }
      },
      required: ['body_area', 'goal'],
      additionalProperties: false
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: wrap('recommend_tape_for_use_case', function (a, data) {
      var primary, extras = [], why, tips = [];
      switch (a.goal) {
        case 'firm_immobilising_support':
          primary = product(data, 'athletic_tape');
          extras.push(product(data, 'foam_underwrap'));
          why = 'Rigid athletic tape restricts joint movement for firm protection during competition. Apply over foam underwrap to protect the skin.';
          break;
        case 'grip_protection':
          primary = product(data, 'finger_tape');
          why = 'Thin, firm finger tape protects fingers, thumbs and palms from bar friction and calluses without bulk.';
          break;
        case 'compression':
          primary = product(data, 'cohesive_bandage');
          why = 'Self-adhering cohesive wrap gives light compression, holds ice packs and secures dressings without sticking to skin.';
          break;
        default: // support_and_pain_relief, recovery
          primary = product(data, a.experience === 'first_time' ? 'k_tape_precut' : 'k_tape_roll');
          extras.push(product(data, a.experience === 'first_time' ? 'k_tape_roll' : 'k_tape_precut'));
          why = 'Elastic kinesiology tape lifts the skin slightly and supports the muscle or joint while you stay active — the standard choice for ' + (a.condition || 'this kind of pain') + '. Lasts 3-5 days including showers.';
          if (a.experience === 'first_time') tips.push('Precut strips have rounded corners and set lengths — easiest for self-application. Roll tape is more economical once you know the technique.');
      }
      if (a.sweat_or_water) tips.push('TapeGeeks kinesiology tape is waterproof; apply 30-60 minutes before sweating or swimming, rub the tape to activate the adhesive, and round every corner so edges do not lift.');
      if (a.skin_sensitive) {
        tips.push('All TapeGeeks tapes are hypoallergenic, latex-free and drug-free. Do a small 24-hour patch test first; for rigid athletic tape always use foam underwrap.');
        extras.push({ guide: 'Kinesiology tape for sensitive skin', url: abs(data.base, '/blogs/news/kinesiology-tape-sensitive-skin-hypoallergenic') });
      }
      var guides = guidesFor(data, a.body_area, a.condition, 3);
      if (a.body_area !== 'general') guides = guides.concat(guidesFor(data, 'general', 'how to apply', 1));
      var res = {
        recommendation: primary,
        also_consider: extras.filter(Boolean),
        why: why,
        tips: tips,
        taping_guides: guides,
        safety: 'Kinesiology tape is not a substitute for diagnosis. For a serious injury, numbness, or pain that is not improving, see a physiotherapist — TapeGeeks\' free directory: https://www.sportsclinicfinder.com/',
        next_step: 'To add this to the cart use Shopify\'s update_cart tool with the product URL above.'
      };
      showPanel('tape recommendation', [primary.name + ' — $' + primary.price_cad + ' CAD', why], { url: primary.url, label: 'View product' });
      return res;
    })
  };

  // ---------- Tool 2: find_taping_guide ----------
  var findGuide = {
    name: 'find_taping_guide',
    title: 'Find a TapeGeeks taping guide',
    description:
      'Find TapeGeeks\' step-by-step kinesiology taping guides and injury articles for a body area or condition (e.g. "how do I tape a shoulder", "IT band taping", "shin splints"). ' +
      'Returns guide titles and URLs on tapegeeks.com. Use this for how-to and technique questions. ' +
      'Do NOT use it to pick a product (use recommend_tape_for_use_case) or for sleep/breathing topics (use size_breathe_plus).',
    inputSchema: {
      type: 'object',
      properties: {
        body_area: { type: 'string', enum: BODY_AREAS, description: 'Body area. Use "general" for technique basics (tension, cuts, showering, removal, contraindications).' },
        condition: { type: 'string', description: 'Optional condition keyword to rank results, e.g. "plantar fasciitis", "tennis elbow", "sprain".' },
        limit: { type: 'integer', minimum: 1, maximum: 6, description: 'Max guides to return (default 3).' }
      },
      required: ['body_area'],
      additionalProperties: false
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: wrap('find_taping_guide', function (a, data) {
      var guides = guidesFor(data, a.body_area, a.condition, a.limit || 3);
      var basics = a.body_area === 'general' ? [] : guidesFor(data, 'general', 'tension', 2);
      var res = {
        guides: guides,
        technique_basics: basics,
        all_guides_chart: abs(data.base, '/blogs/news/kt-tape-application-chart'),
        product_for_these_guides: product(data, 'k_tape_roll'),
        note: guides.length ? null : 'No specific guide for that area yet — the application chart covers every body region.'
      };
      if (guides.length) showPanel('taping guide', [guides[0].title], { url: guides[0].url, label: 'Open guide' });
      return res;
    })
  };

  // ---------- Tool 3: size_breathe_plus ----------
  var sizeBreathe = {
    name: 'size_breathe_plus',
    title: 'Choose the right Breathe+ nasal strip or mouth tape',
    description:
      'Choose the right TapeGeeks Breathe+ product (kids nasal strips, adult nasal strips, mouth tape, or a kit) for a person\'s age and concern — snoring, congestion, mouth breathing at night, athletic breathing, better sleep. ' +
      'Applies TapeGeeks\' age and safety rules (kids strips are for ages 5+, under 5 needs a pediatrician, mouth tape is for adults without congestion or sleep apnea). ' +
      'Returns the product, URL, price, sizing rationale, safety notes and guide links. Do NOT use for kinesiology or athletic tape questions.',
    inputSchema: {
      type: 'object',
      properties: {
        age: { type: 'integer', minimum: 0, maximum: 120, description: 'Age of the person who will wear it, in years.' },
        concern: { type: 'string', enum: ['snoring', 'nighttime_congestion', 'mouth_breathing_at_night', 'athletic_breathing', 'better_sleep', 'not_sure'], description: 'Main reason they are looking.' },
        has_nasal_congestion_or_sleep_apnea: { type: 'boolean', description: 'True if the person has chronic nasal congestion, a deviated septum, diagnosed sleep apnea or any breathing disorder. Affects mouth-tape suitability.' },
        for_whole_family: { type: 'boolean', description: 'True if buying for several people (adults and kids).' }
      },
      required: ['age', 'concern'],
      additionalProperties: false
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: wrap('size_breathe_plus', function (a, data) {
      var res = { safety: [], guides: guidesFor(data, 'breathing', a.concern, 3) };
      if (a.age < 5) {
        res.recommendation = null;
        res.reason = 'TapeGeeks Breathe+ Kids Nasal Strips are designed for ages 5 and up. For a child under 5, please consult their pediatrician before using any nasal strip; TapeGeeks does not recommend mouth tape for children.';
        res.safety.push('Under 5: pediatrician first.');
        showPanel('Breathe+ sizing', ['Under 5 — please check with a pediatrician first.']);
        return res;
      }
      if (a.age <= 12) {
        res.recommendation = product(data, 'kids_nasal');
        res.reason = 'Ages 5-12: Kids Nasal Strips are sized for smaller noses with a gentler adhesive. Helps nighttime congestion, mouth breathing during sleep and snoring in children. Fun Unicorn & Dino designs available.';
        res.safety.push('Mouth tape is not recommended for children.');
      } else {
        var apnea = !!a.has_nasal_congestion_or_sleep_apnea;
        if (a.concern === 'athletic_breathing') {
          res.recommendation = product(data, 'adult_nasal');
          res.reason = 'Adult nasal strips open the nasal passages instantly — the go-to for easier breathing during running and training.';
        } else if (apnea) {
          res.recommendation = product(data, 'adult_nasal');
          res.reason = 'With congestion, a deviated septum or sleep apnea, start with adult nasal strips only. Mouth tape is NOT suitable without a doctor\'s approval.';
          res.safety.push('Do not use mouth tape with nasal congestion, breathing disorders or sleep apnea unless your doctor approves.');
        } else if (a.concern === 'snoring' || a.concern === 'mouth_breathing_at_night' || a.concern === 'better_sleep') {
          res.recommendation = product(data, 'mouth_tape');
          res.also_consider = [product(data, 'adult_nasal')];
          res.reason = 'For adult snoring and mouth breathing, Breathe+ Mouth Tape gently keeps the lips closed so you breathe through the nose; pairing it with an adult nasal strip (the "Breathe+ sleep stack") works best if the nose feels tight.';
          res.safety.push('Mouth tape is for adults who can breathe comfortably through the nose. Skip it if you are congested tonight.');
        } else {
          res.recommendation = product(data, 'adult_nasal');
          res.also_consider = [product(data, 'mouth_tape')];
          res.reason = 'Adult nasal strips are the simplest starting point for nighttime congestion or general sleep quality; add mouth tape later if mouth breathing persists.';
        }
        if (a.age >= 13 && a.age < 18) res.safety.push('Teens: nasal strips are fine; mouth tape is intended for adults.');
      }
      if (a.for_whole_family) { res.kit = product(data, 'family_sleep_kit'); res.kit_note = 'Family Sleep Kit bundles mouth tape + adult strips + kids strips.'; }
      res.safety.push('All Breathe+ products are drug-free, latex-free and hypoallergenic. Do a small skin patch test the first night.');
      res.next_step = 'To add it to the cart use Shopify\'s update_cart tool with the product URL.';
      if (res.recommendation) showPanel('Breathe+ sizing', [res.recommendation.name + ' — $' + res.recommendation.price_cad + ' CAD', res.reason], { url: res.recommendation.url, label: 'View product' });
      return res;
    })
  };

  // ---------- Tool 4: get_shipping_and_duties_info ----------
  var shippingInfo = {
    name: 'get_shipping_and_duties_info',
    title: 'TapeGeeks shipping, delivery time and duties',
    description:
      'Get TapeGeeks shipping facts for a destination country: which warehouse ships the order (Oakville, Ontario for Canada; Lewiston, New York for the USA), whether duties or customs apply (none for CA or US — both are domestic), free-shipping thresholds, delivery times, returns policy and contact. ' +
      'Use for any "where do you ship from / how long / will I pay duties / is shipping free" question. Do NOT use for order tracking (use Shopify\'s order tools).',
    inputSchema: {
      type: 'object',
      properties: {
        country: { type: 'string', enum: ['CA', 'US', 'OTHER'], description: 'Destination: CA = Canada, US = United States, OTHER = anywhere else.' },
        order_subtotal: { type: 'number', minimum: 0, description: 'Optional cart subtotal in the local currency, to say whether shipping will be free.' }
      },
      required: ['country'],
      additionalProperties: false
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: wrap('get_shipping_and_duties_info', function (a, data) {
      var s = data.shipping[a.country] || data.shipping.OTHER;
      var res = Object.assign({ country: a.country }, s, { returns: data.shipping.returns, contact: data.shipping.contact });
      if (typeof a.order_subtotal === 'number' && (a.country === 'CA' || a.country === 'US')) {
        var th = a.country === 'CA' ? 50 : 35;
        res.free_shipping_applies = a.order_subtotal > th;
        res.free_shipping_gap = a.order_subtotal > th ? 0 : Math.round((th - a.order_subtotal) * 100) / 100;
      }
      showPanel('shipping info', [s.ships_from ? 'Ships from ' + s.ships_from : s.note, s.duties ? 'Duties: ' + s.duties : '']);
      return res;
    })
  };

  // ---------- Tool 5: get_wholesale_program_info ----------
  var wholesaleInfo = {
    name: 'get_wholesale_program_info',
    title: 'TapeGeeks wholesale / clinic program',
    description:
      'Explain the TapeGeeks wholesale program for clinics, gyms, pharmacies, teams and businesses: who qualifies, minimum quantities, how to apply, Faire availability and who to contact. ' +
      'Use when a physiotherapist, chiropractor, trainer, gym, store or team asks about bulk, B2B, practitioner or wholesale pricing. ' +
      'Returns the discount tiers (40% off; 50% off after $1,000 spend), minimums and the application path. Do NOT use for consumer multi-pack pricing (use search_catalog for the 6-pack or bulk roll).',
    inputSchema: {
      type: 'object',
      properties: {
        business_type: { type: 'string', enum: ['clinic', 'gym_or_crossfit', 'pharmacy_or_retail', 'team_school_or_club', 'corporate_wellness', 'other'], description: 'Type of business asking.' },
        monthly_volume_rolls: { type: 'integer', minimum: 0, description: 'Optional estimated rolls per month.' }
      },
      required: ['business_type'],
      additionalProperties: false
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: wrap('get_wholesale_program_info', function (a, data) {
      var w = data.wholesale;
      var res = {
        eligible: true,
        who_qualifies: w.eligible,
        minimums: w.minimums,
        how_to_apply: abs(data.base, w.apply_url),
        faire: w.faire,
        contact: w.contact,
        pricing: w.pricing_note,
        distributor: w.distributor,
        consumer_alternative: [product(data, 'k_tape_6pack'), product(data, 'k_tape_bulk')]
      };
      if (a.business_type === 'clinic') res.free_resource = abs(data.base, w.clinic_resource);
      if (typeof a.monthly_volume_rolls === 'number' && a.monthly_volume_rolls >= 10) res.note = 'At ' + a.monthly_volume_rolls + ' rolls/month you clear the wholesale minimum — apply at the link and mention that volume.';
      showPanel('wholesale program', [w.minimums], { url: res.how_to_apply, label: 'Apply for a wholesale account' });
      return res;
    })
  };

  [recommendTape, findGuide, sizeBreathe, shippingInfo, wholesaleInfo].forEach(function (t) {
    try { mc.registerTool(t, { signal: controller.signal }); }
    catch (e) { try { console.warn('[TapeGeeks WebMCP] registerTool failed for', t.name, e); } catch (_) {} }
  });

  // Expose for debugging / evals: window.tgWebMCP.abort() removes all five tools.
  window.tgWebMCP = { abort: function () { controller.abort(); }, tools: ['recommend_tape_for_use_case', 'find_taping_guide', 'size_breathe_plus', 'get_shipping_and_duties_info', 'get_wholesale_program_info'] };
})();
